/**
 * Minimal product-analytics wrapper.
 *
 * This is the single integration point for emitting product-analytics events.
 * Each `track` call fans out to every configured product-analytics backend:
 *
 * - **PostHog** — via `window.posthog` (the instance config exposes the
 *   credentials, see `app/types/instance.ts`). Safe no-op until a client is
 *   loaded on `window`.
 * - **Userpilot** — via `trackUserpilot`, so the same events drive Userpilot
 *   flow / checklist targeting and activation analytics. Safe no-op until the
 *   Userpilot SDK is initialized (env var + LD flag, see `userpilot-sdk.ts`).
 *
 * Adding a backend here means every existing `track(...)` call site emits to it
 * automatically — no call-site changes.
 */

import { trackUserpilot } from "@/userpilot-sdk";

/**
 * The surface a user action came from, sent as the `source` prop on product
 * events (NG-2054). One resource event fires whichever surface made the change,
 * and `source` tells the surfaces apart: `list` opens or acts on a record from a
 * list or table, `table_inline` is an edit made in a table cell, `bulk` is a
 * bulk-bar action, and `notification_bell` is the header bell's popover.
 * Typing it catches a misspelled surface at compile time. Store methods default
 * it to `page`, the resource's own page, so only other surfaces pass it, and
 * adding an event never changes a page's call sites.
 */
export type AnalyticsSource =
  | "page"
  | "list"
  | "gantt"
  | "timeline"
  | "calendar"
  | "capacity_grid"
  | "board"
  | "quick_view"
  | "notification_bell"
  | "side_panel"
  | "table_inline"
  | "bulk"
  | "import";

type AnalyticsClient = { capture: (event: string, props?: Record<string, unknown>) => void };

function getClient(): AnalyticsClient | undefined {
  if (typeof window === "undefined") return undefined;
  const client = (window as { posthog?: AnalyticsClient }).posthog;
  return typeof client?.capture === "function" ? client : undefined;
}

/**
 * Emit a product-analytics event to every configured backend. Safe no-op for
 * any backend that isn't configured yet. Each backend is isolated so one
 * throwing never starves the others (and `trackUserpilot` reports its own
 * errors to Sentry).
 */
export function track(event: string, props?: Record<string, unknown>): void {
  try {
    getClient()?.capture(event, props);
  } catch (err) {
    // Analytics must never break the app — degrade instead of throwing.
    console.warn("[analytics] capture event failed", { event }, err);
  }
  trackUserpilot(event, props);
}
